import { useCallback, useMemo, useSyncExternalStore } from "react";

// URL-as-state hook.
//
// All view state that should survive a reload, be shareable via link, and play
// nicely with the back button lives in `window.location.search`. Components read
// via `useUrlParam(key, codec)` and write via the returned setter. Codecs handle
// parse/serialize and what "default" means (defaults are encoded as the absence
// of the param, which keeps URLs clean).
//
// Internals:
// - A single module-level subscriber set is notified on `popstate` (browser
//   back/forward) and on a custom `URL_CHANGE_EVENT` dispatched by our own
//   setters. The browser does not emit `popstate` for `pushState`/`replaceState`,
//   so the custom event is what keeps in-page updates in sync across components.
// - `useSyncExternalStore` subscribes each hook instance and snapshots
//   `window.location.search` (a string), so React only re-renders on actual URL
//   changes — derived values are recomputed during render.

const URL_CHANGE_EVENT = "urlstate:change";

const listeners = new Set<() => void>();
const notify = () => {
  for (const l of listeners) l();
};

function subscribe(onChange: () => void) {
  listeners.add(onChange);
  if (listeners.size === 1 && typeof window !== "undefined") {
    window.addEventListener("popstate", notify);
    window.addEventListener(URL_CHANGE_EVENT, notify);
  }
  return () => {
    listeners.delete(onChange);
    if (listeners.size === 0 && typeof window !== "undefined") {
      window.removeEventListener("popstate", notify);
      window.removeEventListener(URL_CHANGE_EVENT, notify);
    }
  };
}

function getSearch() {
  return typeof window === "undefined" ? "" : window.location.search;
}

export type HistoryMode = "push" | "replace";

export type UrlCodec<T> = {
  parse: (raw: string | null) => T;
  // `serialize` returns `null` to mean "remove this param" — that's how defaults
  // stay invisible in the URL.
  serialize: (value: T) => string | null;
};

export function setUrlParams(
  updates: Record<string, string | null>,
  history: HistoryMode = "push",
): void {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  let changed = false;
  for (const [key, value] of Object.entries(updates)) {
    const current = params.get(key);
    // Don't push duplicate entries when nothing actually changes — protects the
    // back button from being clogged by no-op writes (e.g. a debounced effect
    // firing with the same value).
    if ((value ?? null) === (current ?? null)) continue;
    if (value === null) params.delete(key);
    else params.set(key, value);
    changed = true;
  }
  if (!changed) return;

  const qs = params.toString();
  const url =
    window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash;
  if (history === "push") window.history.pushState(null, "", url);
  else window.history.replaceState(null, "", url);
  window.dispatchEvent(new Event(URL_CHANGE_EVENT));
}

export function setUrlParam(
  key: string,
  serialized: string | null,
  history: HistoryMode = "push",
): void {
  setUrlParams({ [key]: serialized }, history);
}

export function useUrlParam<T>(
  key: string,
  codec: UrlCodec<T>,
  opts: { history?: HistoryMode } = {},
): [T, (next: T) => void] {
  const search = useSyncExternalStore(subscribe, getSearch, getSearch);
  // Memoise the parsed value so consumers get a stable reference across
  // renders. Critical for codecs that return fresh objects (Set, Array): an
  // unstable reference would re-fire any useEffect that depends on it.
  const value = useMemo(
    () => codec.parse(new URLSearchParams(search).get(key)),
    [search, key, codec],
  );
  const history = opts.history ?? "push";
  const setValue = useCallback(
    (next: T) => {
      setUrlParam(key, codec.serialize(next), history);
    },
    [key, codec, history],
  );
  return [value, setValue];
}

// Codecs. Define at module scope (or memoize) so the hook's setter identity
// stays stable across renders.

export const stringCodec = (defaultValue = ""): UrlCodec<string> => ({
  parse: (raw) => raw ?? defaultValue,
  serialize: (value) => (value === defaultValue ? null : value),
});

export const booleanCodec = (defaultValue = false): UrlCodec<boolean> => ({
  parse: (raw) => (raw === null ? defaultValue : raw === "1"),
  serialize: (value) => (value === defaultValue ? null : value ? "1" : "0"),
});

export const enumCodec = <T extends string>(
  values: readonly T[],
  defaultValue: T,
): UrlCodec<T> => ({
  parse: (raw) =>
    raw !== null && (values as readonly string[]).includes(raw)
      ? (raw as T)
      : defaultValue,
  serialize: (value) => (value === defaultValue ? null : value),
});

export const stringSetCodec = (
  separator = ",",
): UrlCodec<ReadonlySet<string>> => ({
  parse: (raw) => {
    if (raw === null || raw === "") return new Set();
    return new Set(raw.split(separator).filter((s) => s.length > 0));
  },
  serialize: (value) => {
    if (value.size === 0) return null;
    return Array.from(value).join(separator);
  },
});
